import { TweenLite } from 'gsap';
import getEventHandlerResize from '../ResizeHandler';
import { BREAKPOINTS, ANIMATIONS, CONFIG } from './../config';
import * as helpers from './../../tools/helpers';

const BACK_BUTTON_CLASS = 'js-back-button';
const BACK_BUTTON_SELECTOR = `.${BACK_BUTTON_CLASS}`;
const LI_ACTIVE_CLASS = 'bfv-navigation__navigation-entry--active';
const LI_ACTIVE_SELECTOR = `.${LI_ACTIVE_CLASS}`;

const LI_OPEN_FLYOUT_CLASS = 'bfv-navigation__navigation-entry--open-flyout';
const LI_OPEN_FLYOUT_SELECTOR = `.${LI_OPEN_FLYOUT_CLASS}`;

class BfvNavigation {
  constructor(options = {}) {
    this.$root = options.element;
    this.$navigation = this.$root.querySelector('.js-navigation');
    this.$mainUl = this.$root.querySelector('.js-main-ul');
    this.$activeUl = this.$mainUl;
    this.$flyouts = this.$root.querySelectorAll('.js-flyout');
    this.$clickableMenuEntries = this.$mainUl.querySelectorAll('li.bfv-navigation__navigation-entry--has-subentries > a');
    this.$clickableMenuEntries = Array.prototype.slice.call(this.$clickableMenuEntries);
    this.$navigationWrapper = this.$root.parentNode;
    this.currentBreakpointKey = getEventHandlerResize().getCurrentViewport();
    this.currentWindowHeight = null;
    this.currentWindowWidth = null;
    this.isMobileOpen = false;
  }

  init() {
    this.bindClickEvents();

    window.addEventListener(CONFIG.events.RESIZE, () => {
      if (this.currentBreakpointKey === BREAKPOINTS.large) {
        this.desktopCalculateFlyoutDimension();
      } else {
        const currentWidth = helpers.getWindowSize().width;

        if (this.currentWindowWidth !== currentWidth) {
          this.currentWindowWidth = currentWidth;
          this.mobileCloseMenu();
        }
      }
    });

    window.addEventListener(CONFIG.events.VIEWPORT_CHANGED, (event) => {
      helpers.freezeBodyScroll();
      this.currentBreakpointKey = event.detail.viewport;
      this.setupModule();
      helpers.unfreezeBodyScroll();
    });

    window.addEventListener(CONFIG.events.BFV_OPEN_MOBILE_MENU, () => {
      if (this.currentBreakpointKey !== BREAKPOINTS.large) {

        if (!helpers.isBodyScrollFreezed()) {
          this.mobileOpenMenu();
        } else {
          this.mobileCloseMenu();
        }
      }
    });

    this.setupModule();
  }

  /**
   * Remove styles of other viewports,
   * set correct active entries,
   * set correct dimensions
   */
  setupModule() {
    // Mobile
    if (this.currentBreakpointKey !== BREAKPOINTS.large) {
      this.desktopResetStyles();

      this.mobileFollowActivePath(this.$mainUl);
      this.mobileSlideInActiveUl();
    } else { // Desktop
      this.mobileStyleReset();

      this.desktopCalculateFlyoutDimension(true);
    }
  }

  bindClickEvents() {
    this.$clickableMenuEntries.forEach((menuEntry) => {
      menuEntry.addEventListener('click', this.handleMenuEntryClick.bind(this));
    });
  }

  /**
   * bind click event to back button
   * @param $ul
   */
  bindBackButtonClickEventsForUl($ul) {
    const $recentlyAddedBackButton = $ul.querySelector(BACK_BUTTON_SELECTOR);
    $recentlyAddedBackButton.addEventListener('click', this.mobileSlideOutActiveUl.bind(this));
  }

  /**
   * Set correct entry active on each menu entry
   * @param $clickedLi
   */
  toggleActiveClassForListEntries($clickedLi) {
    // 1. Remove class form clicked sibling
    Array.prototype.slice.call($clickedLi.parentNode.children).forEach(($li) => {
      if ($li.classList.contains(LI_ACTIVE_CLASS)) {
        $li.classList.remove(LI_ACTIVE_CLASS);
      }
    });

    // 2. Set class to clicked LI
    $clickedLi.classList.add(LI_ACTIVE_CLASS);
  }

  /**
   * Set correct entry active on menu entry click; toggle flyout / sub-levels if necessary
   * @param ev
   */
  handleMenuEntryClick(ev) {
    ev.preventDefault();

    this.$activeLi = helpers.findParentNodeType(ev.target, 'li');

    /* Toggle Active Class List */
    this.toggleActiveClassForListEntries(this.$activeLi);

    if (this.currentBreakpointKey === BREAKPOINTS.large) {
      const $activeLi = helpers.findParentNodeType(ev.target, 'li');
      const $clickedFlyout = $activeLi.querySelector('.js-flyout');

      // Click - first level
      if ($clickedFlyout) {
        if (!this.$activeFlyout || !$clickedFlyout.isEqualNode(this.$activeFlyout)) {
          /* Show */

          helpers.freezeBodyScroll();

          // 1  if already open, close it
          if (this.$activeFlyout) {
            this.desktopFadeOutFlyout(this.$activeFlyout, ANIMATIONS.default.DURATION);
          }

          // 2 - fade in new flyout
          this.desktopCalculateFlyoutDimension(true);
          this.$activeFlyout = $clickedFlyout;
          this.desktopFadeInFlyout();
        } else {
          /* Hide */

          helpers.unfreezeBodyScroll();

          this.$activeLi.classList.remove(LI_ACTIVE_CLASS);

          this.desktopFadeOutFlyout(this.$activeFlyout);
          this.$activeFlyout = null;
        }
      }
    } else {
      this.$activeUl = this.$activeLi.querySelector('.js-sublevel-ul');
      this.mobileSlideInActiveUl();
    }
  }

  /** Desktop functions
   *  Remove all desktop (flyout) styles, since we don't need them on mobile viewports
   * */
  desktopResetStyles() {
    const $activeLiFromMainUl = this.$mainUl.querySelector(LI_OPEN_FLYOUT_SELECTOR);
    if ($activeLiFromMainUl) {
      $activeLiFromMainUl.classList.remove(LI_OPEN_FLYOUT_CLASS);
    }

    this.$flyouts.forEach(($flyout) => {
      $flyout.removeAttribute('style');
    });
    this.$activeFlyout = null;
  }

  /**
   * Set Top and Height for desktop flyout (navigation wrapper)
   * @param forceCalculation
   */
  desktopCalculateFlyoutDimension(forceCalculation) {
    const currentHeight = helpers.getWindowSize().height;

    if (this.currentWindowHeight !== currentHeight || forceCalculation) {
      this.currentWindowHeight = currentHeight;

      const mainUlHeight = this.$mainUl.clientHeight;
      const height = this.currentWindowHeight - (this.$mainUl.getBoundingClientRect().top + mainUlHeight);

      TweenLite.set(this.$flyouts, {
        top: `${mainUlHeight}px`,
        height: `${height}px`,
      });
    }
  }

  /**
   * Show desktop flyout (navigation wrapper) animated
   */
  desktopFadeInFlyout() {
    const $liFromMainUl = helpers.findParentNodeType(this.$activeFlyout, 'li');
    $liFromMainUl.classList.add(LI_OPEN_FLYOUT_CLASS);

    TweenLite.to(this.$activeFlyout, ANIMATIONS.default.DURATION, {
      ease: ANIMATIONS.default.CURVE,
      opacity: 1,
      display: 'block',
    });
  }

  /**
   * Hide desktop flyout (navigation wrapper) animated
   */
  desktopFadeOutFlyout($flyout, delay = 0) {
    const $liFromMainUl = helpers.findParentNodeType($flyout, 'li');
    $liFromMainUl.classList.remove(LI_OPEN_FLYOUT_CLASS);

    if (delay) {
      TweenLite.set($flyout, {
        'z-index': 50,
      });
    }

    TweenLite.to($flyout, ANIMATIONS.default.DURATION, {
      ease: ANIMATIONS.default.CURVE,
      opacity: 0,
      delay,
      onComplete: () => {
        TweenLite.set($flyout, {
          display: 'none',
        });
      },
    });
  }

  /** Mobile functions
   *  Remove mobile styles and sub-levels, since we don't need them on viewport L
   */
  mobileStyleReset() {
    TweenLite.killTweensOf(this.$navigation);
    this.$navigation.removeAttribute('style');
    this.$mainUl.removeAttribute('style');
    this.$navigationWrapper.removeAttribute('style');
    helpers.unfreezeBodyScroll();

    const $sublevels = this.$mainUl.querySelectorAll('.js-sublevel-ul');
    $sublevels.forEach(($subUl) => {
      $subUl.removeAttribute('style');
    });
  }

  /**
   * Open the navigation on viewports XS and M
   */
  mobileOpenMenu() {
    helpers.freezeBodyScroll();
    this.currentWindowWidth = helpers.getWindowSize().width;

    const headerHeight = this.$navigationWrapper.parentNode.querySelector('.bfv-header').clientHeight;

    TweenLite.to(this.$navigationWrapper, ANIMATIONS.default.DURATION, {
      ease: ANIMATIONS.default.CURVE,
      height: helpers.getWindowSize().height - headerHeight,
    });

    this.isMobileOpen = true;
  }

  /**
   * Close the navigation on viewports XS and M
   */
  mobileCloseMenu() {
    if (this.isMobileOpen && // only close menu if open
      this.$navigationWrapper.classList.contains('js-navigation-wrapper')) { // and if we are in wrapper, otherwise no header exists to reopen it

      this.isMobileOpen = false;
      helpers.unfreezeBodyScroll();

      TweenLite.set(this.$navigationWrapper, {
        height: this.$navigationWrapper.clientHeight,
      });

      TweenLite.to(this.$navigationWrapper, ANIMATIONS.default.DURATION, {
        ease: ANIMATIONS.default.CURVE,
        height: 0,
      });
    }
  }

  /**
   * Determine recursively which elements are active on each (sub-)level and set it active
   * @param $ul
   */
  mobileFollowActivePath($ul) {
    const $activeListEntry = $ul.parentNode.querySelector(`${LI_ACTIVE_SELECTOR}`);
    if (!$activeListEntry) {
      return;
    }

    const $upcomingUl = $activeListEntry.querySelector('.bfv-navigation__navigation__ul-menu');

    if ($activeListEntry && $upcomingUl) {
      this.$activeUl = $upcomingUl;
      this.mobileActivateMenuPath(this.$activeUl);
      this.mobileFollowActivePath($upcomingUl);
    }
  }

  /**
   * Set menu entry active and add back button
   * @param $ul
   */
  mobileActivateMenuPath($ul) {
    this.mobileAddBackButton($ul);

    TweenLite.set($ul, {
      left: 0,
    });
  }

  /**
   * Show mobile Navigation animated
   */
  mobileSetNavigationHeight() {
    const activeUlHeight = this.$activeUl.clientHeight;

    TweenLite.to(this.$navigation, ANIMATIONS.default.DURATION, {
      ease: ANIMATIONS.default.CURVE,
      height: activeUlHeight,
    });
  }

  /**
   * Show mobile navigation sub-level animated
   */
  mobileSlideInActiveUl() {
    this.mobileAddBackButton();

    TweenLite.to(this.$activeUl, ANIMATIONS.default.DURATION, {
      ease: ANIMATIONS.default.CURVE,
      left: 0,
    });

    TweenLite.to(this.$navigationWrapper, ANIMATIONS.default.DURATION, {
      ease: ANIMATIONS.default.CURVE,
      scrollTop: 0,
    });

    this.mobileSetNavigationHeight();
  }

  /**
   * Hide mobile navigation sub-level animated
   * @param ev
   */
  mobileSlideOutActiveUl(ev) {
    ev.preventDefault();

    const $liBackButton = helpers.findParentNodeType(ev.target, 'li');
    const $parentLi = helpers.findParentNodeType($liBackButton, 'li');
    $parentLi.classList.remove(LI_ACTIVE_CLASS);

    const $slideOutUl = this.$activeUl;
    this.$activeUl = helpers.findParentNodeType($slideOutUl, 'ul');

    TweenLite.to($slideOutUl, ANIMATIONS.default.DURATION, {
      ease: ANIMATIONS.default.CURVE,
      left: '100%',
    });

    this.mobileSetNavigationHeight();
  }

  /**
   * Add back button to mobile Menu
   * @param $ul
   */
  mobileAddBackButton($ul = null) {
    const $targetUl = $ul || this.$activeUl;

    if (!$targetUl.querySelector(BACK_BUTTON_SELECTOR)) {
      const $parentLi = helpers.findParentNodeType($targetUl, 'li');

      if (!$parentLi) {
        /* root level - there is no parent li */
        return;
      }

      const backButtonMarkup = $parentLi.cloneNode();
      backButtonMarkup.appendChild($parentLi.querySelector('a').cloneNode(true));
      backButtonMarkup.classList.remove(LI_ACTIVE_CLASS);
      backButtonMarkup.classList.add(BACK_BUTTON_CLASS);

      $targetUl.insertBefore(backButtonMarkup, $targetUl.firstChild);
      this.bindBackButtonClickEventsForUl($targetUl);
    }
  }
}

export default BfvNavigation;



// WEBPACK FOOTER //
// ./src/javascript/main/BfvNavigation/index.js